import React, { createContext, useContext, useState, useEffect } from 'react';

const ThemeContext = createContext(undefined);

// Available primary colors
export const primaryColors = [
  { name: 'Mavi', value: '199 89% 48%', class: 'bg-cyan-500' },
  { name: 'Yeşil', value: '142 76% 36%', class: 'bg-green-600' },
  { name: 'Mor', value: '262 83% 58%', class: 'bg-purple-500' },
  { name: 'Turuncu', value: '25 95% 53%', class: 'bg-orange-500' },
  { name: 'Kırmızı', value: '0 84% 60%', class: 'bg-red-500' },
  { name: 'Pembe', value: '330 81% 60%', class: 'bg-pink-500' },
  { name: 'İndigo', value: '239 84% 67%', class: 'bg-indigo-500' },
  { name: 'Teal', value: '168 76% 42%', class: 'bg-teal-500' },
];

// Preset theme packages
export const themePackages = [
  {
    id: 'corporate',
    name: 'Kurumsal',
    description: 'Profesyonel ve güvenilir',
    mode: 'light',
    primaryColor: '217 91% 60%', // Blue
    preview: {
      primary: '#3b82f6',
      secondary: '#64748b',
      accent: '#f1f5f9'
    }
  },
  {
    id: 'modern',
    name: 'Modern',
    description: 'Şık ve çağdaş',
    mode: 'dark',
    primaryColor: '262 83% 58%', // Purple
    preview: {
      primary: '#a855f7',
      secondary: '#6366f1',
      accent: '#1e1b4b'
    }
  },
  {
    id: 'nature',
    name: 'Doğa',
    description: 'Huzurlu ve ferah',
    mode: 'light',
    primaryColor: '142 76% 36%', // Green
    preview: {
      primary: '#16a34a',
      secondary: '#84cc16',
      accent: '#f0fdf4'
    }
  },
  {
    id: 'warm',
    name: 'Sıcak',
    description: 'Enerjik ve canlı',
    mode: 'light',
    primaryColor: '25 95% 53%', // Orange
    preview: {
      primary: '#f97316',
      secondary: '#eab308',
      accent: '#fff7ed'
    }
  },
  {
    id: 'minimal',
    name: 'Minimal',
    description: 'Sade ve temiz',
    mode: 'light',
    primaryColor: '220 9% 46%', // Slate gray
    preview: {
      primary: '#64748b',
      secondary: '#94a3b8',
      accent: '#f8fafc'
    }
  },
  {
    id: 'midnight',
    name: 'Gece',
    description: 'Koyu ve zarif',
    mode: 'dark',
    primaryColor: '199 89% 48%', // Cyan
    preview: {
      primary: '#06b6d4',
      secondary: '#0ea5e9',
      accent: '#0c4a6e'
    }
  },
  {
    id: 'rose',
    name: 'Gül',
    description: 'Yumuşak ve şık',
    mode: 'light',
    primaryColor: '330 81% 60%', // Pink
    preview: {
      primary: '#ec4899',
      secondary: '#f472b6',
      accent: '#fdf2f8'
    }
  },
  {
    id: 'ocean',
    name: 'Okyanus',
    description: 'Derin ve sakin',
    mode: 'dark',
    primaryColor: '168 76% 42%', // Teal
    preview: {
      primary: '#14b8a6',
      secondary: '#2dd4bf',
      accent: '#134e4a'
    }
  }
];

export function ThemeProvider({ children }) {
  const [theme, setTheme] = useState(() => {
    const saved = localStorage.getItem('payflow-theme');
    return saved || 'light';
  });
  
  const [primaryColor, setPrimaryColor] = useState(() => {
    const saved = localStorage.getItem('payflow-primary-color');
    return saved || '199 89% 48%'; // Default cyan/blue
  });

  useEffect(() => {
    const root = window.document.documentElement;
    
    // Apply theme class
    root.classList.remove('light', 'dark');
    root.classList.add(theme);
    
    // Save to localStorage
    localStorage.setItem('payflow-theme', theme);
  }, [theme]);

  useEffect(() => {
    const root = window.document.documentElement;
    
    // Apply primary color
    root.style.setProperty('--primary', primaryColor);
    root.style.setProperty('--ring', primaryColor);
    
    // Calculate accent based on primary
    const [h, s, l] = primaryColor.split(' ');
    const accentL = theme === 'dark' ? '20%' : '95%';
    root.style.setProperty('--accent', `${h} 50% ${accentL}`);
    root.style.setProperty('--accent-foreground', `${h} ${s} 35%`);
    
    // Save to localStorage
    localStorage.setItem('payflow-primary-color', primaryColor);
  }, [primaryColor, theme]);

  const toggleTheme = () => {
    setTheme(prev => prev === 'light' ? 'dark' : 'light');
  };

  const applyThemePackage = (packageId) => {
    const pkg = themePackages.find(p => p.id === packageId);
    if (pkg) {
      setTheme(pkg.mode);
      setPrimaryColor(pkg.primaryColor);
      localStorage.setItem('payflow-theme-package', packageId);
    }
  };

  const getCurrentPackage = () => {
    return localStorage.getItem('payflow-theme-package') || null;
  };

  return (
    <ThemeContext.Provider value={{ 
      theme, 
      setTheme, 
      toggleTheme, 
      primaryColor, 
      setPrimaryColor,
      primaryColors,
      themePackages,
      applyThemePackage,
      getCurrentPackage
    }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme() {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
}
